import { CommandIcon, CornerDownLeftIcon } from '@langchain/untitled-ui-icons';

import { useEffect, useMemo, useState } from 'react';
import { useBlocker } from 'react-router-dom';

import { useExampleCrud } from '@/components/ExampleCrudPane/hooks';
import { getDataType } from '@/components/ExampleCrudPane/utils';
import useToast from '@/components/Toast/useToast';
import { Button, Text } from '@/design-system';
import { useOrganizationId, useRunRules } from '@/hooks/useSwr';
import { PostFeedbackBody, RuleSchema } from '@/types/schema';
import { cn } from '@/utils/tailwind';

import { KeyboardShortcut } from '../KeyboardShortcutsSidePane';
import { useEvaluatorFeedbackKeysFromRules } from '../Playground/utils/use-evaluator-feedback-keys';
import { AnnotationQueueInner } from './AnnotationQueue';
import { ConfirmationModal } from './ConfirmationModal';
import { AlignQueueRubricForm } from './components/AlignQueueRubricForm';
import { AnnotationQueueProps } from './types';
import { useConfirmationModal } from './utils/useConfirmationModal';

export const AlignAnnotationQueue = (props: AnnotationQueueProps) => {
  const { createToast } = useToast();
  const organizationId = useOrganizationId();

  const { annotationQueue } = props;

  const { data: rules } = useRunRules(
    annotationQueue?.run_rule_id ? { id: [annotationQueue.run_rule_id] } : null
  );

  const aligningRule = rules?.[0];
  const ruleAsArray: RuleSchema[] = useMemo(
    () => (aligningRule ? [aligningRule] : []),
    [aligningRule]
  );
  const { evaluatorFeedbackKeys } = useEvaluatorFeedbackKeysFromRules({
    rules: ruleAsArray,
    organizationId,
  });

  const [localFeedbackMap, setLocalFeedbackMap] = useState<
    Record<string, PostFeedbackBody>
  >({});
  const [fewShotExplanation, setFewShotExplanation] = useState<string>('');

  const alignmentFeedbackKey = evaluatorFeedbackKeys?.[0];
  const alignmentFeedbackScore =
    alignmentFeedbackKey && localFeedbackMap[alignmentFeedbackKey]?.score;

  const datasetDataType = getDataType(undefined, props.currentRun);
  const { loading: addToDatasetLoading, onSubmit } = useExampleCrud({
    datasetDataType,
    onError: (e) => {
      createToast({
        title: 'Error adding example to dataset',
        description: e,
        error: true,
      });
    },
  });

  const handleAddAlignmentExample = () => {
    if (
      props.currentRun &&
      alignmentFeedbackKey &&
      aligningRule?.corrections_dataset_id &&
      alignmentFeedbackScore != null
    ) {
      onSubmit({
        inputs: JSON.stringify({
          input: props.referenceExample?.inputs ?? props.currentRun.inputs,
          output: props.currentRun.outputs,
          ...(props.referenceExample?.outputs && {
            referenceOutput: props.referenceExample.outputs,
          }),
        }),
        outputs: JSON.stringify({
          [alignmentFeedbackKey]: alignmentFeedbackScore,
          few_shot_explanation: fewShotExplanation,
        }),
        source_run_id: props.currentRun.id,
        dataset_id: aligningRule.corrections_dataset_id,
      });
      setHasEdited(false);
      props.handleMarkDone();
    }
  };

  const handleFeedbackChange = (
    feedbackMap: Record<string, PostFeedbackBody>
  ) => {
    setLocalFeedbackMap(feedbackMap);
    setHasEdited(true);
  };

  const {
    isConfirmationModalOpen,
    setIsConfirmationModalOpen,
    handleMoveToEnd,
    handleLeftClick,
    handleRightClick,
    setDontShowConfirmationAgain,
    setHasEdited,
    hasEdited,
  } = useConfirmationModal(
    // don't show confirmation when adding alignment example
    () => {},
    props.handleMoveToEnd,
    props.handleLeftClick,
    props.handleRightClick
  );

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (currentLocation.pathname === nextLocation.pathname) {
      return false;
    }
    return hasEdited;
  });

  useEffect(() => {
    if (blocker.state === 'blocked') {
      setIsConfirmationModalOpen(() => blocker.proceed);
    }
  }, [blocker.state, setIsConfirmationModalOpen, blocker.proceed]);

  return (
    <>
      <ConfirmationModal
        isConfirmationModalOpen={isConfirmationModalOpen}
        setIsConfirmationModalOpen={setIsConfirmationModalOpen}
        setDontShowConfirmationAgain={setDontShowConfirmationAgain}
      />
      <AnnotationQueueInner
        {...props}
        handleMarkDone={handleAddAlignmentExample}
        handleMoveToEnd={handleMoveToEnd}
        handleLeftClick={handleLeftClick}
        handleRightClick={handleRightClick}
        setHasEdited={setHasEdited}
        onAddToDataset={onSubmit}
        isAddingToDataset={addToDatasetLoading}
        hideDatasetSelector={true}
        allowEdits={false}
        renderMarkDoneButton={() => (
          <Button
            color="primary"
            size="md"
            onClick={handleAddAlignmentExample}
            disabled={alignmentFeedbackScore == null}
          >
            <Text variant="md">Add to reference dataset</Text>
            <KeyboardShortcut
              keys={[
                <CommandIcon className="h-3 w-3" />,
                <CornerDownLeftIcon className="h-3 w-3" />,
              ]}
              className={cn(
                'gap-1 border-brand-subtle bg-brand-secondary py-1 text-brand-primary',
                addToDatasetLoading && 'invisible'
              )}
              soloClassName="h-3 w-3"
            />
          </Button>
        )}
        rubricFormRenderer={({ annotationQueue, runId, sessionId }) => (
          <AlignQueueRubricForm
            annotationQueue={annotationQueue}
            runId={runId}
            sessionId={sessionId}
            feedbackMap={localFeedbackMap}
            onFeedbackChange={handleFeedbackChange}
            isLoading={false}
            fewShotExplanation={fewShotExplanation}
            onFewShotExplanationChange={setFewShotExplanation}
          />
        )}
      />
    </>
  );
};
